import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Download, MagnifyingGlass, Calendar } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function toISODate(d) {
  return d.toISOString().split('T')[0];
}

export default function PickingHistoryPage() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = new Date();
  const monthAgo = new Date(); monthAgo.setDate(monthAgo.getDate() - 30);
  const [dateFrom, setDateFrom] = useState(toISODate(monthAgo));
  const [dateTo, setDateTo] = useState(toISODate(today));
  const [search, setSearch] = useState('');

  useEffect(() => { fetchHistory(); }, []);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('warehouse_token');
      const res = await axios.get(`${API}/picking/completed`, {
        params: { date_from: dateFrom, date_to: dateTo },
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(res.data);
    } catch (err) {
      toast.error('Failed to load picking history');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCsv = async () => {
    try {
      const token = localStorage.getItem('warehouse_token');
      const res = await axios.get(`${API}/picking/completed.csv`, {
        params: { date_from: dateFrom, date_to: dateTo },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `picking_history_${dateFrom}_${dateTo}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (err) {
      toast.error('CSV export failed');
    }
  };

  const filtered = tasks.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (t.item_code || '').toLowerCase().includes(q) ||
      (t.qc_number || '').toLowerCase().includes(q) ||
      (t.batch_number || '').toLowerCase().includes(q) ||
      (t.reference || '').toLowerCase().includes(q) ||
      (t.from_location?.code || '').toLowerCase().includes(q)
    );
  });

  const totalPallets = filtered.reduce((s, t) => s + (t.pallets || t.quantity || 0), 0);
  const totalCartons = filtered.reduce((s, t) => s + ((t.pallets || t.quantity || 0) * (t.cartons || 0)), 0);

  return (
    <div className="p-8" data-testid="picking-history-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="heading text-3xl font-black text-[#09090B] mb-2" data-testid="picking-history-title">
            Picking History
          </h1>
          <p className="text-[#52525B]">Audit completed picks by date. Export to CSV for supervisor reports.</p>
        </div>
        <button
          onClick={handleDownloadCsv}
          className="btn-primary flex items-center gap-2"
          data-testid="picking-history-csv-button"
        >
          <Download size={18} weight="bold" />
          Export CSV
        </button>
      </div>

      <div className="card mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="date_from">Date From</Label>
            <Input id="date_from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="picking-history-date-from" />
          </div>
          <div>
            <Label htmlFor="date_to">Date To</Label>
            <Input id="date_to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="picking-history-date-to" />
          </div>
          <div>
            <Label htmlFor="search">Search</Label>
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
              <Input id="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Item, QC, batch, ref, loc" className="pl-9 mono" data-testid="picking-history-search" />
            </div>
          </div>
          <div className="flex items-end">
            <button onClick={fetchHistory} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="picking-history-apply-button">
              <Calendar size={16} weight="bold" />
              Apply
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="card bg-[#16A34A]/5 border-[#16A34A]/20">
          <div className="flex items-center gap-3">
            <CheckCircle size={24} className="text-[#16A34A]" weight="duotone" />
            <div>
              <p className="text-sm text-[#52525B]">Completed Picks</p>
              <p className="heading text-2xl font-black text-[#09090B]" data-testid="history-total-count">{filtered.length}</p>
            </div>
          </div>
        </div>
        <div className="card bg-[#007AFF]/5 border-[#007AFF]/20">
          <div className="flex items-center gap-3">
            <div>
              <p className="text-sm text-[#52525B]">Total Pallets</p>
              <p className="heading text-2xl font-black text-[#09090B] mono" data-testid="history-total-pallets">{totalPallets}</p>
            </div>
          </div>
        </div>
        <div className="card bg-[#F59E0B]/5 border-[#F59E0B]/20">
          <div className="flex items-center gap-3">
            <div>
              <p className="text-sm text-[#52525B]">Total Cartons</p>
              <p className="heading text-2xl font-black text-[#09090B] mono" data-testid="history-total-cartons">{totalCartons}</p>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card text-center py-12">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-12" data-testid="picking-history-empty">
          <p className="text-[#52525B]">No completed picks in this range.</p>
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm" data-testid="picking-history-table">
            <thead>
              <tr className="border-b border-[#E4E4E7] text-left">
                <th className="p-3 text-xs font-medium text-[#52525B] uppercase">Completed</th>
                <th className="p-3 text-xs font-medium text-[#52525B] uppercase">Item Code</th>
                <th className="p-3 text-xs font-medium text-[#52525B] uppercase">QC / Shade</th>
                <th className="p-3 text-xs font-medium text-[#52525B] uppercase">Batch</th>
                <th className="p-3 text-xs font-medium text-[#52525B] uppercase">Reference</th>
                <th className="p-3 text-xs font-medium text-[#52525B] uppercase">Quantity</th>
                <th className="p-3 text-xs font-medium text-[#52525B] uppercase">From</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA]" data-testid="picking-history-row">
                  <td className="p-3 mono text-xs text-[#52525B]">{t.completed_at ? t.completed_at.slice(0, 16).replace('T', ' ') : '-'}</td>
                  <td className="p-3 mono font-bold text-[#007AFF]">{t.item_code || '-'}</td>
                  <td className="p-3 mono text-[#09090B]">{t.qc_number || '-'}</td>
                  <td className="p-3 mono text-[#09090B]">{t.batch_number || '-'}</td>
                  <td className="p-3 mono text-[#09090B]">{t.reference || '-'}</td>
                  <td className="p-3 mono font-bold text-[#09090B]">{(t.pallets || t.quantity)} plts{t.cartons ? ` × ${t.cartons} ctns` : ''}</td>
                  <td className="p-3 mono font-bold text-[#16A34A]">{t.from_location?.code || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
